import { useLocation, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { formatWebinarTime } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, CalendarPlus, Video, Gift } from "lucide-react";

function gcalLink(w) {
  const start = new Date(w.start_utc);
  const end = new Date(start.getTime() + w.duration_min * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const params = new URLSearchParams({
    action: "TEMPLATE", text: w.title, dates: `${fmt(start)}/${fmt(end)}`,
    details: `Join: ${w.meeting_url}`, location: "Online",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function WebinarConfirmed() {
  const { state } = useLocation();
  const { timezone } = useApp();
  const navigate = useNavigate();

  if (!state?.webinar) {
    return (
      <div className="max-w-xl mx-auto px-6 py-32 text-center text-slate-500">
        No registration found. <Link to="/webinars" className="text-asf-orange">Browse webinars</Link>
      </div>
    );
  }
  const { registration, webinar } = state;
  const t = formatWebinarTime(webinar.start_utc, timezone);

  const attendAndOffer = async () => {
    try { await api.attendWebinar(registration.id); } catch {}
    toast.success("Marked as attended — your exclusive offer is unlocked!");
    navigate(`/offer/${webinar.topic}`);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="webinar-confirmed">
      <div className="h-16 w-16 rounded-full bg-green-500/15 grid place-items-center mx-auto mb-6">
        <CheckCircle2 className="h-9 w-9 text-green-400" />
      </div>
      <div className="asf-overline mb-2">Registration confirmed</div>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">You're registered!</h1>

      <div className="asf-card !translate-y-0 hover:!translate-y-0 p-8 mt-8 text-left">
        <h2 className="font-display text-xl font-semibold">{webinar.title}</h2>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Date</dt><dd>{t.dayLabel}</dd></div>
          <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Time</dt><dd>{t.time} {t.tzShort} (your local time)</dd></div>
          <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Duration</dt><dd>{webinar.duration_min} minutes</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Host</dt><dd>{webinar.host}</dd></div>
        </dl>
        <div className="flex flex-wrap gap-3 mt-6">
          <a href={webinar.meeting_url} target="_blank" rel="noreferrer" data-testid="join-link" className="btn-secondary px-5 py-3 flex items-center gap-2"><Video className="h-4 w-4" /> Join Link</a>
          <a href={gcalLink(webinar)} target="_blank" rel="noreferrer" data-testid="add-calendar" className="btn-secondary px-5 py-3 flex items-center gap-2"><CalendarPlus className="h-4 w-4" /> Add to Calendar</a>
        </div>
      </div>

      <div className="mt-8 rounded-2xl bg-asf-orange/10 border border-asf-orange/30 p-6">
        <Gift className="h-8 w-8 text-asf-orange mx-auto mb-3" />
        <p className="text-slate-700">As a registered attendee you're eligible for an exclusive webinar-only course bundle.</p>
        <button data-testid="view-offer-btn" onClick={attendAndOffer} className="btn-primary px-8 py-4 mt-5">See My Exclusive Offer</button>
      </div>
    </div>
  );
}
